import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { Container, Card, Form, Button, Row, Col, Alert, Image, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export default function ProfileSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fullName, setFullName] = useState("");
  const [jobPosition, setJobPosition] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getProfile();
  }, []);

  async function getProfile() {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      let { data, error } = await supabase
        .from("profiles")
        .select(`full_name, job_position, avatar_url`)
        .eq("id", user.id)
        .single();
      if (data) {
        setFullName(data.full_name || "");
        setJobPosition(data.job_position || "");
        setAvatarUrl(data.avatar_url || "");
      }
    }
    setLoading(false);
  }

  // Handle the File Upload
  async function uploadAvatar(event) {
    try {
      setUploading(true);
      if (!event.target.files || event.target.files.length === 0) throw new Error("Select an image.");

      const file = event.target.files[0];
      const fileExt = file.name.split(".").pop();
      const { data: { user } } = await supabase.auth.getUser();
      const fileName = `${user.id}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload file to 'avatars' bucket
      let { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      setAvatarUrl(publicUrl);
      setMessage({ type: "success", text: "Image uploaded! Save changes to finalize." });
    } catch (error) {
      setMessage({ type: "danger", text: error.message });
    } finally {
      setUploading(false);
    }
  }

  async function updateProfile(e) {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    const updates = { id: user.id, full_name: fullName, job_position: jobPosition, avatar_url: avatarUrl, updated_at: new Date() };
    let { error } = await supabase.from("profiles").upsert(updates);
    if (error) setMessage({ type: "danger", text: error.message });
    else setMessage({ type: "success", text: "Profile updated!" });
  }

  if (loading) return <Container className="mt-5">Loading...</Container>;

  return (
    <Container className="mt-5 mb-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card className="shadow-sm border-0">
            <Card.Body className="p-4 text-center">
              <h3 className="fw-bold mb-4">Profile Settings</h3>
              
              {/* Avatar Preview & Upload */}
              <div className="mb-4 position-relative">
                <Image 
                  src={avatarUrl || "https://via.placeholder.com/150"} 
                  roundedCircle 
                  style={{ width: "120px", height: "120px", objectFit: "cover" }}
                  className="mb-3 border shadow-sm"
                />
                <div>
                  <Form.Label htmlFor="avatar-upload" className="btn btn-outline-primary btn-sm">
                    {uploading ? <Spinner animation="border" size="sm" /> : "Change Photo"}
                  </Form.Label>
                  <Form.Control 
                    type="file" 
                    id="avatar-upload" 
                    accept="image/*" 
                    onChange={uploadAvatar} 
                    className="d-none"
                    disabled={uploading}
                  />
                </div>
              </div>

              {message && <Alert variant={message.type}>{message.text}</Alert>}

              <Form onSubmit={updateProfile} className="text-start">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">Full Name</Form.Label>
                  <Form.Control type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">Job Position</Form.Label>
                  <Form.Control type="text" value={jobPosition} onChange={(e) => setJobPosition(e.target.value)} />
                </Form.Group>

                <div className="d-grid gap-2">
                  <Button variant="primary" type="submit">Save Changes</Button>
                  <Button variant="light" onClick={() => navigate("/dashboard")}>Back</Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}